import React, { useEffect, useState, useMemo, useCallback } from 'react';
import SendbirdUIKitProvider from '@sendbird/uikit-react/SendbirdProvider';
import '@sendbird/uikit-react/dist/index.css';
import { usePathname } from 'expo-router';

import { useSessionHandler } from '@/shared/hooks/useSendbirdSessionHandler';
import { useSendbirdToken } from '@/shared/hooks/useSendbirdToken';
import useConfigStore from '@/stores/useConfigStore';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { sendbirdColorSet } from '@/shared/styles/sendbirdTheme';

export const SendbirdProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { environment } = useConfigStore();
  const { user, isLoadingUser } = useGetUser();
  const { fetchSendbirdSessionToken, isLoadingSendbirdToken } =
    useSendbirdToken();
  const sessionHandler = useSessionHandler();
  const { isMobile } = useBreakpoint();
  const [token, setToken] = useState<string>();
  const pathname = usePathname();

  // Memoize onboarding check to prevent unnecessary re-renders
  const isInOnboarding = useMemo(
    () => pathname.includes('onboarding'),
    [pathname],
  );

  // Memoize user props to prevent Sendbird SDK reinitializations
  // These only change when the actual values change, not when user object reference changes
  const userId = useMemo(() => String(user?.id), [user?.id]);
  const nickname = useMemo(() => user?.firstName, [user?.firstName]);
  const profileUrl = useMemo(() => user?.profilePicUrl, [user?.profilePicUrl]);

  useEffect(() => {
    if (!user?.id || isInOnboarding) return;
    fetchSendbirdSessionToken()
      .then(setToken)
      .catch(() => {
        setToken(undefined);
      });
  }, [user?.id, isInOnboarding, fetchSendbirdSessionToken]);

  const isLoading = isLoadingUser || isLoadingSendbirdToken;

  // Memoize configureSession callback to prevent unnecessary Sendbird reinitialization
  const configureSession = useCallback(() => sessionHandler, [sessionHandler]);

  if (isLoading) return <FullScreenLoader />;

  return (
    <SendbirdUIKitProvider
      appId={environment.sendbird.applicationId}
      userId={userId}
      nickname={nickname}
      profileUrl={profileUrl}
      accessToken={token}
      colorSet={sendbirdColorSet}
      breakpoint={isMobile}
      configureSession={configureSession}
      sdkInitParams={{
        newInstance: true,
      }}
      uikitOptions={{
        groupChannelList: {
          enableTypingIndicator: true,
        },
        groupChannel: {
          enableVoiceMessage: false,
          enableTypingIndicator: true,
          input: {
            enableDocument: true,
            camera: {
              enablePhoto: true,
              enableVideo: true,
            },
            gallery: {
              enablePhoto: true,
              enableVideo: true,
            },
          },
        },
      }}
    >
      {children}
    </SendbirdUIKitProvider>
  );
};
